import { useEffect, useCallback } from 'react'

type ShortcutHandler = () => void

interface ShortcutConfig {
  key: string
  handler: ShortcutHandler
  ctrl?: boolean
  shift?: boolean
  alt?: boolean
  meta?: boolean
  description?: string
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean
}

export function useKeyboardShortcuts(
  shortcuts: ShortcutConfig[],
  options: UseKeyboardShortcutsOptions = {}
) {
  const { enabled = true } = options

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = event.target as HTMLElement
      const isInput =
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable

      if (isInput) return

      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase()
        const ctrlMatches = !!shortcut.ctrl === (event.ctrlKey || event.metaKey)
        const shiftMatches = !!shortcut.shift === event.shiftKey
        const altMatches = !!shortcut.alt === event.altKey

        if (keyMatches && ctrlMatches && shiftMatches && altMatches) {
          event.preventDefault()
          shortcut.handler()
          return
        }
      }
    },
    [shortcuts]
  )

  useEffect(() => {
    if (!enabled) return

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [enabled, handleKeyDown])
}

// Common keyboard shortcuts descriptions for help display
export const SHORTCUT_KEYS = {
  NEW_ENTRY: 'n',
  PREV_PERIOD: 'ArrowLeft',
  NEXT_PERIOD: 'ArrowRight',
  TODAY: 't',
  DAY_VIEW: '1',
  WEEK_VIEW: '2',
  MONTH_VIEW: '3',
  CUSTOM_VIEW: '4',
  HELP: '?',
} as const
